import dayjs from 'dayjs'

import type {
    SelfEmployedTransactionDetails,
    SelfEmployedTransactionDetailsStatusEnumKey,
    SelfEmployedTransactionDetailsTypeEnumKey,
} from '@/shared/api'
import { currencyParser } from '@/shared/lib/currency-parser'

type TAmountSign = 'keep' | 'minus' | 'plus'

export type TSheetLine = {
    description?: string
    title: string
    value: string
}

type TSheetView = {
    amount: null | string
    fees: null | TSheetLine[]
    main: null | TSheetLine
    subtitle?: string
    title: string
    total: null | TSheetLine
}

const sheetTitles: Record<SelfEmployedTransactionDetailsTypeEnumKey, string> = {
    achivment: 'Награда за цель',
    compensation: 'Компенсация за подачу',
    dotaion: 'Выплата за заказ',
    order: 'Доход за заказ',
    payout: 'Вывод средств',
    refund: 'Возврат пассажиру',
}

const sheetSubtitles: Partial<
    Record<
        SelfEmployedTransactionDetailsTypeEnumKey,
        Partial<Record<SelfEmployedTransactionDetailsStatusEnumKey, string>>
    >
> = {
    order: {
        canceled: 'Оформлен возврат средств',
        success: 'Начислен',
        waiting: 'В обработке',
    },
    payout: {
        canceled: 'Какая-то причина отклонения',
        waiting: 'В обработке',
    },
}

const formatKopecks = (amount: number, sign: TAmountSign = 'keep') => {
    const value = currencyParser(amount)

    if (sign === 'plus') {
        return `${+value} ₽`
    }

    if (sign === 'minus') {
        return `${-value} ₽`
    }

    return `${value} ₽`
}

const hasOrderBreakdown = (status?: SelfEmployedTransactionDetailsStatusEnumKey) =>
    status === 'success' || status === 'waiting'

const amountSign = (
    type: SelfEmployedTransactionDetailsTypeEnumKey,
    status?: SelfEmployedTransactionDetailsStatusEnumKey,
): null | TAmountSign => {
    if (type === 'order' || type === 'payout') {
        if (status === 'success') {
            return type === 'order' ? 'plus' : 'minus'
        }

        if (status === 'waiting' || status === 'canceled') {
            return 'keep'
        }

        return null
    }

    return type === 'refund' ? 'minus' : 'plus'
}

const mainLine = (details: SelfEmployedTransactionDetails): null | TSheetLine => {
    const { amount, status, type } = details
    const reward = details.details?.reward

    if (type === 'payout' || (type === 'order' && !hasOrderBreakdown(status))) {
        return null
    }

    if (type === 'dotaion') {
        return {
            description: `Выполнено ${reward?.order_count} заказов`,
            title: 'Итого',
            value: formatKopecks(amount, 'plus'),
        }
    }

    if (type === 'achivment') {
        return {
            description: `${reward?.order_count} поездок с ${dayjs(reward?.start_at).format('DD.MM')} по ${dayjs(reward?.end_at).format('DD.MM')}`,
            title: 'Достижение цели',
            value: formatKopecks(amount, 'plus'),
        }
    }

    if (type === 'compensation') {
        return {
            description: 'Оплата картой',
            title: 'Компенсация за подачу',
            value: formatKopecks(amount),
        }
    }

    return {
        description: 'Оплата картой',
        title: 'Стоимость поездки',
        value: formatKopecks(amount),
    }
}

const feeLines = (details: SelfEmployedTransactionDetails): null | TSheetLine[] => {
    const { status, type } = details
    const showFees =
        (type === 'order' && hasOrderBreakdown(status)) ||
        type === 'compensation' ||
        type === 'refund'

    if (!showFees) {
        return null
    }

    const serviceTitle = type === 'order' ? 'Платёж Drivee' : 'Комиссия Drivee'
    const serviceFee = details.details?.service_fee
    const vat = details.details?.vat?.amount
    const lines: TSheetLine[] = []

    if (serviceFee) {
        lines.push({ title: serviceTitle, value: formatKopecks(serviceFee, 'minus') })
    }

    if (vat) {
        lines.push({ title: 'НДС', value: formatKopecks(vat, 'minus') })
    }

    return lines
}

const totalLine = (details: SelfEmployedTransactionDetails): null | TSheetLine => {
    const { amount, status, type } = details
    const showTotal =
        (type === 'order' && hasOrderBreakdown(status)) ||
        type === 'compensation' ||
        type === 'refund'

    if (!showTotal) {
        return null
    }

    return {
        description: 'С учетом вычетов',
        title: 'Итого',
        value: formatKopecks(amount),
    }
}

export const getSheetView = (details: SelfEmployedTransactionDetails): TSheetView => {
    const sign = amountSign(details.type, details.status)

    return {
        amount: sign ? formatKopecks(details.amount, sign) : null,
        fees: feeLines(details),
        main: mainLine(details),
        subtitle: details.status ? sheetSubtitles[details.type]?.[details.status] : undefined,
        title: sheetTitles[details.type],
        total: totalLine(details),
    }
}
